import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import UvCollection from "@/components/landing/UvCollection";

const chapters = [
  {
    num: "01",
    title: "Press-On Nails with Kit",
    copy: "A complete at-home ritual — nails, glue, file and prep, curated in one keepsake box.",
    img: "/kit-nails-2.webp",
  },
  {
    num: "02",
    title: "Press-On Nails with UV Light",
    copy: "Salon-grade hold at home. Cure your set under a pocket UV light for weeks of wear.",
    img: "/uv-light-nails.jpeg",
  },
  {
    num: "03",
    title: "UV Press-On Nail Wrap",
    copy: "Feather-light gel wraps that cure under UV — a second skin of colour, zero drying time.",
    img: "/uv-nail-wrap.webp",
  },
];

export default function Verticals() {
  const [uvOpen, setUvOpen] = useState(false);
  return (
    <section id="verticals" className="px-6 py-24 lg:px-12 lg:py-32" data-testid="verticals-section">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-20 max-w-2xl"
        >
          <p className="mb-6 text-xs font-semibold uppercase tracking-[0.4em] text-[#6F665F]">
            The Collection
          </p>
          <h2 className="font-display text-4xl leading-none tracking-tight md:text-6xl" data-testid="verticals-heading">
            Three ways to wear <em className="italic text-[#D8AFA4]">your</em> label
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 gap-16 md:grid-cols-3 md:gap-x-12 md:gap-y-24">
          {chapters.map((c, i) => (
            <motion.article
              key={c.num}
              initial={{ opacity: 0, y: 48 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className={`${i % 2 === 1 ? "md:mt-28" : ""} ${c.num === "02" ? "cursor-pointer" : ""}`}
              onClick={c.num === "02" ? () => setUvOpen(true) : undefined}
              data-testid={`vertical-card-${c.num}`}
            >
              <div className="group relative overflow-hidden border border-[#26221F]/20">
                <img
                  src={c.img}
                  alt={c.title}
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  data-testid={`vertical-image-${c.num}`}
                />
                <span className="absolute left-4 top-4 rounded-full bg-[#FAF7F2]/90 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#26221F]">
                  Coming Soon
                </span>
                {c.num === "02" && (
                  <span
                    className="absolute bottom-4 right-4 flex items-center gap-2 rounded-full bg-[#26221F] px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#FAF7F2] transition-colors duration-300 group-hover:bg-[#D8AFA4] group-hover:text-[#26221F]"
                    data-testid="vertical-open-uv"
                  >
                    View the Collection <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                )}
              </div>
              <div className="mt-8 flex items-baseline gap-6">
                <span className="font-display text-6xl italic leading-none text-[#D8AFA4] md:text-7xl">
                  {c.num}
                </span>
                <div>
                  <h3 className="font-display text-2xl tracking-tight md:text-3xl" data-testid={`vertical-title-${c.num}`}>
                    {c.title}
                  </h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#6F665F]">{c.copy}</p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
      <UvCollection open={uvOpen} onClose={() => setUvOpen(false)} />
    </section>
  );
}
